import { ProviderGroupItemSummary } from './SmartSendBulkActionApplier';

/**
 * The Verify endpoint returns a flat `Breakdown` dictionary that mixes group-level keys
 * (`Breakdown.Subtotal`) with per-item keys (`Item.{itemId}.Total.Subtotal`). Only the per-item
 * family is handled here.
 *
 * The API writes `Subtotal` for every priced item but only writes `Tax` / `DutiesAndTaxes` when
 * they are non-zero, so a missing Subtotal means "Verify did not price this item" while a missing
 * Tax on a priced item means "zero". That asymmetry is what the rules below are built on.
 */
const ItemBreakdownFields = {
  Subtotal: 'Subtotal',
  Tax: 'Tax',
  DutiesAndTaxes: 'DutiesAndTaxes'
} as const;

const itemBreakdownKey = (itemId: string, field: string) => `Item.${itemId}.Total.${field}`;

/**
 * Replaces the locally estimated prices on one service row with the verified ones.
 *
 * All-or-nothing per row: unless every item behind the row was priced by Verify, the row keeps its
 * estimate, because summing verified figures for some items and estimates for others would produce
 * a total that matches neither.
 *
 * Returns the original object when nothing changes, so React can skip re-rendering the row.
 */
export function ApplyVerifiedBreakdownToSummary(
  summary: ProviderGroupItemSummary,
  breakdown: Record<string, number> | null | undefined
): ProviderGroupItemSummary {
  if (!breakdown || !summary.ItemIds?.length) {
    return summary;
  }

  const priced = summary.ItemIds.every(
    (id) => typeof breakdown[itemBreakdownKey(id, ItemBreakdownFields.Subtotal)] === 'number'
  );

  if (!priced) {
    return summary;
  }

  const sum = (field: string) =>
    summary.ItemIds.reduce((total, id) => total + (breakdown[itemBreakdownKey(id, field)] ?? 0), 0);

  const TotalPriceExVat = sum(ItemBreakdownFields.Subtotal);
  const TotalVat = sum(ItemBreakdownFields.Tax);
  const Duties = sum(ItemBreakdownFields.DutiesAndTaxes);

  return {
    ...summary,
    TotalPriceExVat,
    TotalVat,
    Duties,
    TotalPrice: TotalPriceExVat + TotalVat + Duties
  };
}

/** Row-wise {@link ApplyVerifiedBreakdownToSummary}. Returns the original array when nothing changes. */
export function ApplyVerifiedBreakdownToSummaries(
  summaries: ProviderGroupItemSummary[],
  breakdown: Record<string, number> | null | undefined
): ProviderGroupItemSummary[] {
  if (!breakdown || !summaries?.length) {
    return summaries;
  }

  const applied = summaries.map((s) => ApplyVerifiedBreakdownToSummary(s, breakdown));

  return applied.some((s, i) => s !== summaries[i]) ? applied : summaries;
}
